/**
 * PillarBrief — the brief at the top of each pillar page (DESIGN.md
 * "Layout", `.brief.pillar` in carbon/02 and 03): the verdict column
 * (BriefHero at pillar size) beside a pillar score tile (ScoreTile: swatch,
 * 6rem numeral over "/ 100", delta tag). No verdict on the tile (D22) and no
 * score breakdown. The tile column is 360px (320px below 1312px) and the
 * brief stacks below 1056px.
 *
 * `brief` takes any composer's output: BriefHero runs it through
 * toActionBrief(), so this frame never depends on the composers' field names
 * beyond the byline and delta it turns into provenance and the delta tag.
 *
 * Props are unchanged from the editorial version; `tone` now picks the
 * tile's swatch and `components` is accepted but no longer drawn.
 */

import type { PostureBrief } from '@/lib/posture-brief'
import type { PillarTone } from '@/components/Dashboard/v2/PillarShell'
import { formatRelativeTime } from '@/lib/brief-helpers'
import { BriefHero } from '@/components/carbon/BriefHero'
import { Marked } from '@/components/carbon/Marked'
import { ScoreTile } from '@/components/carbon/ScoreTile'
import { Tag } from '@/components/carbon/Tag'
import { TONE_CATEGORY, type BriefHeroButtonAction, type Category } from '@/components/carbon/types'

interface ScoreComponent {
  label: string
  /** Display weight string, e.g. "20%". */
  weight: string
  /** Score value (0-100). */
  value: number
  /** Legacy fill utility; unused since the Carbon tile draws no breakdown. */
  fillClass: string
}

interface Props {
  brief: PostureBrief
  /** Score (0-100). Pass `null` to show a dash. Ignored when `aside` is provided. */
  score: number | null
  /** Accepted for compatibility; the Carbon pillar tile has no breakdown. */
  components: ScoreComponent[]
  /** The pillar: picks the score tile's swatch. */
  tone: PillarTone
  isLoading?: boolean
  /** Replaces the score tile, on pages that are not pillars in the Guardian
   *  composite (a 0-100 tile would falsely signal "this rolls into the score"). */
  aside?: React.ReactNode
  /** Hide the delta tag when there is no genuine baseline to compare against
   *  yet (a newly onboarded tenant), so we never draw a fabricated trend. */
  hideDelta?: boolean
  /** One helper line under the provenance, e.g. which pillar a non-pillar page
   *  rolls up into. `**bold**` marks the pillar name. */
  footerNote?: string
  /** The score tile's title. Defaults to "Guardian Score" (the composite);
   *  pillar pages name their own score ("Best Practice Score"). */
  scoreLabel?: string
  /** ISO timestamp of the latest sync of this page's data. Adds an
   *  "Updated N ago" segment to the provenance line; "" or unparseable reads
   *  "Awaiting first sync." Omit (or null) for no segment. */
  lastRefreshedAt?: string | null
  /** The tertiary beside the brief's primary action (mockup 02's "Export baseline report"),
   *  normally a `ButtonLink kind="tertiary"` or a tertiary `Button`. */
  secondaryAction?: React.ReactNode
  /** A button primary in place of the brief's route link (a mutation such as starting an audit). */
  primaryAction?: BriefHeroButtonAction
  /** The whole provenance line, replacing the composer byline and the `lastRefreshedAt` segment
   *  (D21: "Assessed by Guardian at 06:12 · 142 controls checked"). `**bold**` is not parsed here. */
  provenance?: React.ReactNode
  /** Helper text in the score tile's meta row, beside the delta (mockup 03's "Weighted by severity"). */
  scoreMeta?: React.ReactNode
}

const DEFAULT_LABEL = 'Guardian Score'

/** The swatch for a named pillar score; none for the Guardian composite. */
function tileCategory(label: string, tone: PillarTone): Category | undefined {
  if (label === DEFAULT_LABEL) return undefined
  // Microsoft Secure Score is not a Guardian pillar; it has its own neutral mark.
  if (/secure score/i.test(label)) return 'secure'
  return TONE_CATEGORY[tone]
}

/** "+7 in 30 days", "−2.5 in 7 days", "No change in 30 days". */
function deltaText(delta: { value: number; days: number }): string {
  const rounded = Math.round(delta.value * 10) / 10
  const span = `in ${delta.days} day${delta.days === 1 ? '' : 's'}`
  if (rounded === 0) return `No change ${span}`
  const abs = Number.isInteger(rounded) ? String(Math.abs(rounded)) : Math.abs(rounded).toFixed(1)
  return `${rounded > 0 ? '+' : '−'}${abs} ${span}`
}

export default function PillarBrief({
  brief,
  score,
  tone,
  isLoading,
  aside,
  footerNote,
  scoreLabel,
  lastRefreshedAt,
  hideDelta,
  secondaryAction,
  primaryAction,
  provenance: provenanceOverride,
  scoreMeta,
}: Props) {
  // Provenance: the composer's byline segments, plus the data's age when the
  // page knows it. The brief is today's read; the sync time is the data's age.
  // A page that knows its own assessment line (D21) passes it whole.
  const segments = (brief.byline ?? '').split(' · ').filter(Boolean)
  if (lastRefreshedAt !== undefined && lastRefreshedAt !== null) {
    segments.push(formatRelativeTime(lastRefreshedAt) ?? 'Awaiting first sync.')
  }
  const provenance = provenanceOverride ?? (segments.length ? <Marked text={segments.join(' · ')} /> : undefined)

  const label = scoreLabel ?? DEFAULT_LABEL
  const delta = !hideDelta && brief.delta ? <Tag className="tabular-nums">{deltaText(brief.delta)}</Tag> : undefined

  return (
    <section
      aria-labelledby="pillar-brief-headline"
      className="mt-8 grid grid-cols-1 items-start gap-8 c-lg:grid-cols-[minmax(0,1fr)_minmax(0,320px)] c-xlg:grid-cols-[minmax(0,1fr)_minmax(0,360px)]"
    >
      <div className="min-w-0 break-words">
        <BriefHero
          brief={brief}
          size="pillar"
          headingId="pillar-brief-headline"
          isLoading={isLoading}
          provenance={provenance}
          primaryAction={primaryAction}
          secondaryAction={secondaryAction}
        />
        {footerNote ? (
          <p className="m-0 mt-3 max-w-[56ch] text-helper text-text-helper">
            <Marked text={footerNote} />
          </p>
        ) : null}
      </div>

      {aside ?? (
        <ScoreTile
          title={label}
          category={tileCategory(label, tone)}
          score={score}
          delta={delta}
          meta={scoreMeta}
          isLoading={isLoading}
          className="max-c-lg:max-w-[640px]"
        />
      )}
    </section>
  )
}
